import React, { Component, FunctionComponent } from 'react';
import ReactDOM from 'react-dom';

import routes from 'js/core_pre_webpack/routes';
import { get, post } from 'js/utils/api_utils';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { Work } from 'js/liswp/types';
import {
  renderPingWhenReady,
  onsitePings,
} from 'js/onsite_pings/onsite_pings.wjs';
import { showBulkDownloadModal } from 'js/bulk_download/show_bulk_download_modal';
import SearchPopup from 'js/header/SearchBar/SearchPopup';
import BulkDownloadPing from 'js/liswp/Pings/BulkDownloadPing';
import UserSentimentPopup from 'js/liswp/UserSentimentPopup';
import { recordDismissal } from './utils';
import TranslationModal from 'js/translation/TranslationModal';
import LettersModal from 'js/letters/info_modal/LettersModal';
import { LanguageCode } from 'js/translation/util';
import { ToastProvider } from 'js/design_system/toasts/ToastProvider';
import getQSParams from 'js/utils/getQSParams';
import { startQualaroo, QualarooData } from 'js/qualaroo';
import { fetchPostDownloadModalState } from 'js/core_webpack/sitewide_business_elements/download/reader_message_form.wjs.jsx';
import ChurnedUserReactivationModal from 'js/modals/churned_user_reactivation';
import { SwpBulkFollowTakeover } from 'js/social/shared/big_cards/SwpBulkFollowTakeover';
import { FollowableUser } from 'js/social/shared/types';
import SnackBar from 'js/social/shared/SnackBar';
import KiiCollectionModal from 'js/modals/kii_collection';
import FullScreenPremiumModal from 'js/premium_upgrade/FullScreenPremiumModal';
import { getAutoDownloadParams } from 'js/liswp/auto_download_params';
import { FriendlySutdModal } from 'js/single_work/FriendlySutdModal';
import ImpactHubModal from 'js/modals/ImpactHubModal';
import { lcpFinalized } from 'js/utils/trackCWV';
import {
  PaperClaimingHomeProfileInterruption,
  PaperClaimingHomeProfileInterruptionData,
} from 'js/paper_claiming/PaperClaimingHomeProfileInterruption';
import { abTest as paperClaimingV2AbTest } from 'js/ab_tests/tests/paper_claiming_2026_q1_home_profile_v2';

const portalComponents: { [key: string]: FunctionComponent<any> } = {
  bulk_download_ping: BulkDownloadPing,
};

const INTERRUPTIONS = [
  'bulk_download',
  'abandoned_cart_ping',
  'mentions_view_download_research_interest_milestone_ping',
  'profile_hit_research_interest_overlap_ping',
  'single_work_page_related_works_ping',
  'mentions_view_download_research_interest_ping',
  'readers_ping',
  'bulk_download_ping',
  'bulk_download_books_ping',
  'header_search_popup',
  'user_sentiment_popup',
  'brand_health_survey',
  'big_swing_post_sprint_survey',
  'win_back_ping',
  'translation_modal',
  'letters_information_modal',
  'qualaroo_survey',
  'churned_user_reactivation_modal',
  'swp_bulk_follow_takeover',
  'kii_collection_modal',
  'full_screen_premium_modal',
  'impact_hub_modal',
  'paper_claiming_home_profile_modal',
];

// these pings open after a 5s delay instead of immediately
const DELAY_PINGS = [
  'mentions_view_download_research_interest',
  'readers',
  'bulk_download_books',
  'bulk_download',
];

// used for the special case of
// campaign=claiming-recommended-papers-download-button, where we want to use a
// different trigger for the bulk download upsell checkout page redirect's
// trigger (see the openModal function).
const { campaign } = getQSParams('campaign');

type Interruption = (typeof INTERRUPTIONS)[number];

// TODO: refactor these types based on how they actually get used - call js vs ping vs direct render
type InterruptionType = 'ping' | 'modal' | 'popup' | 'portal' | 'qualaroo';

type Props = {
  workId?: number; // Optional - only required for SWP
  premium: boolean;
  // Page type: 'swp' (single work page), 'home', or 'profile'
  pageType: 'swp' | 'home' | 'profile';
  // For profile page - the user being viewed
  viewedUserId?: number;
  // when given will only try to render this specific interruption
  // ignores lookbacks, although must still have valid data to render
  forceRender: Interruption | null | false;
  // true when the translation modal on the page should automatically appear
  showTranslationModal: boolean;
  considerUserSentimentPopup: boolean;
  // Tiger's July 2023 Teaser Pricing Copy Test in Marketing & Header
  trialPricingCopy?: string;
  // Direct force for friendly SUTD modal - bypasses all interruption logic
  forceFriendlySutdModal?: boolean;
  // Author name for the forced friendly SUTD modal
  authorName?: string;
  // Author ID for the forced friendly SUTD modal
  authorId?: number;
};

type ApiResponse = {
  data: ResponseData;
  decision: string;
  type: InterruptionType;
};

type PingName = keyof typeof onsitePings & keyof typeof portalComponents;

// I'm not sure if this type is complete,
// some interruptions may have more data or other fields
// and I don't think all of these properties are present
// for all interruptions.
type ResponseData = {
  pingArgs?: Record<string, unknown>;
  pingName: PingName;
  routeAction: string;
  routeController: string;
  workId: number;
  workTitle?: string;
  userLanguages?: LanguageCode[];
  variant?: string; // name for interruptions that can have multiple variants
  offer?: 'price' | 'discount';
  value?: string;
  mentions?: number;
  topic?: {
    name: string;
    score: number;
  }; // used for swp_bulk_follow_takeover
  bulkFollowUsers?: FollowableUser[]; // used for swp_bulk_follow_takeover
  trialPrice?: string | null;
  churned?: boolean;
};

type State = {
  decision: Interruption | null; // what to render
  type: InterruptionType | null; // how to render
  data: ResponseData | null; // everything required to render
  renderModal: boolean; // for opening/closing modals via this component's render method
  renderPortal: boolean;
  readerMessageLimitReached: boolean;
  setReaderMessageLimitReached: () => void;
  citerightMarketingModalEligible: boolean;
  showConfirmationSnackbar: boolean;
  qualarooFailed: boolean;
};

const API_PATH = routes.api_v0_interruption_choice_index_path({
  subdomain_param: 'api',
});

const PAPER_CLAIMING_STATUS_PATH =
  routes.status_api_v0_paper_claiming_home_profile_index_path({
    subdomain_param: 'api',
  });
const PAPER_CLAIMING_POLL_INTERVAL_MS = 3000;
const PAPER_CLAIMING_MAX_ATTEMPTS = 10; // 30 seconds total

export default class InterruptionChooser extends Component<Props> {
  state: State = {
    decision: null,
    type: null,
    data: null,
    renderModal: false,
    renderPortal: false,
    readerMessageLimitReached: true,
    setReaderMessageLimitReached: () => {},
    citerightMarketingModalEligible: false,
    showConfirmationSnackbar: false,
    qualarooFailed: false,
  };

  componentDidMount() {
    // Don't fetch interruption choice if we're force-rendering the friendly SUTD modal
    if (!this.props.forceFriendlySutdModal) {
      this.fetchInterruptionChoice();
    }
  }

  fetchInterruptionChoice(skipQualaroo = false) {
    const { pageType, workId, viewedUserId } = this.props;
    const [considerations, interruptionData] = this.considerInterruptions();

    const params: Record<string, unknown> = {
      camelize_keys: true,
      page_type: pageType,
      considerations: considerations,
      hit_id: window.Aedu?.hit_data?.id || null,
      page_width: window.innerWidth,
      skip_qualaroo: skipQualaroo,
      ...interruptionData,
    };

    // Only include work_id for SWP pages
    if (pageType === 'swp' && workId) {
      params.work_id = workId;
      params.page_specific_params = {
        work_id: workId,
      };
    }

    // Include viewed_user_id for profile page
    if (pageType === 'profile' && viewedUserId) {
      params.viewed_user_id = viewedUserId;
    }

    get(API_PATH, params)
      .then(this.handleInterruptionChoiceLoad)
      .catch(() => {
        // 204 No Content responses cause a JSON parse failure in get().
        // Treat any failure as "no interruption to show" so polling can start.
        this.handleInterruptionChoiceLoad(undefined);
      });
  }

  // allows the client to explicitly allow/deny any interruption
  considerInterruptions() {
    const { showTranslationModal, considerUserSentimentPopup, forceRender } =
      this.props;

    const { autoDownload } = getAutoDownloadParams();

    // Truthy check is okay here since we only want to force for a non-empty string.
    if (forceRender) {
      const forceConsiderations: Partial<Record<Interruption, boolean>> = {};
      INTERRUPTIONS.forEach(
        (interruption) =>
          (forceConsiderations[interruption] = interruption === forceRender),
      );
      // TODO: allow admins to set interruption data as well
      return [forceConsiderations, {}];
    }

    const interruptionData: { [key: string]: unknown } = {};
    const considerations: Partial<Record<Interruption, boolean>> = {};

    if (showTranslationModal) {
      considerations['translation_modal'] = true;
    }

    // don't consider showing the bulk download upsell unless auto downloading
    if (!autoDownload) {
      considerations['bulk_download'] = false;
    }

    if (!considerUserSentimentPopup) {
      considerations['user_sentiment_popup'] = false;
    }

    return [considerations, interruptionData];
  }

  render() {
    const { renderModal, renderPortal, decision, data, type } = this.state;

    // Direct force for friendly SUTD modal - bypasses all interruption logic
    // Only applies to SWP pages where workId is required
    if (this.props.forceFriendlySutdModal && this.props.workId != null) {
      return (
        <ToastProvider>
          <FriendlySutdModal
            open={renderModal}
            onClose={() => {
              this.setState({ renderModal: false });
              // Remove the force parameter from URL
              const url = new URL(window.location.href);
              url.searchParams.delete('force_friendly_sutd_modal');
              window.history.replaceState({}, '', url.toString());
            }}
            authorName={this.props.authorName || 'the author'}
            authorId={this.props.authorId || 0}
            workId={this.props.workId}
            onReadPaper={() => {
              this.setState({ renderModal: false });
              // Remove the force parameter from URL
              const url = new URL(window.location.href);
              url.searchParams.delete('force_friendly_sutd_modal');
              window.history.replaceState({}, '', url.toString());
            }}
          />
        </ToastProvider>
      );
    }

    if (decision == null || data == null) return null;

    if (decision === 'header_search_popup') {
      return <SearchPopup />;
    }

    // For the wolf_q1_2024_swp_bulk_follow_takeover A/B test.
    // Displays a snackbar after the user bulk-follows the suggested group.
    if (this.state.showConfirmationSnackbar) {
      return (
        <SnackBar
          message={
            <>
              <span style={{ textDecoration: 'underline' }}>View updates</span>
              &nbsp;from academics you're now following
            </>
          }
          type="success"
          onClick={() => {
            window.location.href = routes.home_url();
          }}
          durationInMs={5000}
          showIcon={false}
        />
      );
    }

    if (type === 'portal' && renderPortal) {
      const Component = portalComponents[decision];
      const pingProps = { ...data.pingArgs, pingName: data.pingName };

      return ReactDOM.createPortal(
        <Component
          onClose={() => {
            this.setState({ renderPortal: false });
            recordDismissal(data.pingName);
          }}
          {...pingProps}
        />,
        document.body,
      );
    }

    if (type === 'modal' && renderModal) {
      switch (decision) {
        case 'swp_bulk_follow_takeover':
          if (data.topic && data.bulkFollowUsers) {
            return (
              <SwpBulkFollowTakeover
                topic={data.topic}
                users={data.bulkFollowUsers}
                onClose={(followingsAdded) => {
                  if (followingsAdded) {
                    this.setState({ showConfirmationSnackbar: true });
                  } else {
                    this.setState({ renderModal: false });
                  }
                }}
              />
            );
          }
          break;
        case 'full_screen_premium_modal':
          return (
            <FullScreenPremiumModal
              trialPrice={data.trialPrice}
              churned={data.churned ?? false}
              onClose={() => this.setState({ renderModal: false })}
            />
          );
        case 'impact_hub_modal':
          return (
            <ImpactHubModal
              open={this.state.renderModal}
              onClose={() => this.setState({ renderModal: false })}
            />
          );
      }
    }

    if (decision === 'kii_collection_modal') {
      return (
        <ToastProvider>
          <KiiCollectionModal
            open={this.state.renderModal}
            onClose={() => this.setState({ renderModal: false })}
            kiiSource={'spider_2024_q4_liswp_kii_v1'}
            headingText="Update your information"
            subheadingText="Sharing can lead to more relevant recommendations and updates."
          />
        </ToastProvider>
      );
    }

    // Home/Profile paper claiming modal - uses simpler PaperClaimingModal
    if (decision === 'paper_claiming_home_profile_modal') {
      return (
        <ToastProvider>
          {this.state.renderModal && (
            <PaperClaimingHomeProfileInterruption
              data={data as unknown as PaperClaimingHomeProfileInterruptionData}
              onComplete={() => this.setState({ renderModal: false })}
            />
          )}
        </ToastProvider>
      );
    }

    if (decision === 'user_sentiment_popup') {
      // NATIONAL PARKS: Please remove this disable and try to fix it if you're here
      // eslint-disable-next-line no-inner-declarations
      function onSubmit(sentiment: number, explanation: string) {
        post(
          routes.api_v0_work_user_sentiments_path({
            // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
            work_id: data.workId,
            sentiment,
            explanation,
            subdomain_param: 'api',
          }),
        );
      }

      return (
        <ImpressionWatcher source="user_sentiment_popup">
          <UserSentimentPopup
            question="Are you finding this article worth reading?"
            onSubmit={onSubmit}
          />
        </ImpressionWatcher>
      );
    }

    if (decision === 'translation_modal') {
      const { work } = data as any;

      return (
        this.state.renderModal && (
          <TranslationModal
            work={work as Work}
            onClose={() => this.setState({ renderModal: false })}
            premium={this.props.premium}
            userLanguages={
              data.userLanguages?.filter((lng) => typeof lng !== 'undefined') ||
              ([] as LanguageCode[])
            }
            source={'loswp-translate-button'}
          />
        )
      );
    }

    if (decision === 'letters_information_modal') {
      return (
        this.state.renderModal && (
          <LettersModal
            onClose={() => this.setState({ renderModal: false })}
            workId={data.workId}
          />
        )
      );
    }

    if (decision === 'churned_user_reactivation_modal') {
      return (
        this.state.renderModal &&
        data &&
        data.offer &&
        data.value && (
          <ChurnedUserReactivationModal
            offer={data.offer}
            value={data.value}
            mentions={data.mentions}
            onClose={() => this.setState({ renderModal: false })}
          />
        )
      );
    }

    return null;
  }

  renderModalWhenLcpFinalized = async () => {
    await lcpFinalized();
    this.setState({ renderModal: true });
    this.openModal();
  };

  // Polls the paper claiming status endpoint until paper suggestions are ready,
  // then feeds the result back through the normal interruption rendering path.
  // Called after the interruption controller returns no-content on home/profile
  // pages when the user is in the v2 paper claiming treatment.
  startPaperClaimingPolling = (
    pageType: 'home' | 'profile',
    viewedUserId: number | undefined,
  ): void => {
    let attempts = 0;

    const poll = (): void => {
      if (attempts >= PAPER_CLAIMING_MAX_ATTEMPTS) return;
      attempts += 1;

      const params: Record<string, unknown> = { page_type: pageType };
      if (pageType === 'profile' && viewedUserId != null) {
        params.viewed_user_id = viewedUserId;
      }

      get(PAPER_CLAIMING_STATUS_PATH, params)
        .then(
          (response: { ready: boolean; data?: ResponseData } | undefined) => {
            if (response?.ready && response.data) {
              this.handleInterruptionChoiceLoad({
                decision: 'paper_claiming_home_profile_modal',
                type: 'modal',
                data: response.data,
              });
            } else {
              setTimeout(poll, PAPER_CLAIMING_POLL_INTERVAL_MS);
            }
          },
        )
        .catch(() => {
          setTimeout(poll, PAPER_CLAIMING_POLL_INTERVAL_MS);
        });
    };

    setTimeout(poll, PAPER_CLAIMING_POLL_INTERVAL_MS);
  };

  handleInterruptionChoiceLoad = (data: ApiResponse | undefined) => {
    if (!data) {
      // No modal to show. On home/profile pages, start polling for paper
      // suggestions if the user is in the v2 paper claiming treatment.
      const { pageType, viewedUserId } = this.props;
      if (
        (pageType === 'home' || pageType === 'profile') &&
        paperClaimingV2AbTest() === 'variant_a'
      ) {
        this.startPaperClaimingPolling(pageType, viewedUserId);
      }
      return;
    }
    this.setState(
      {
        decision: data.decision,
        type: data.type,
        data: data.data,
        renderPortal: data.type === 'portal',
        renderModal: false,
      },
      () => {
        if (data.type === 'modal') {
          if (data.decision === 'full_screen_premium_modal') {
            this.renderModalWhenLcpFinalized();
          } else {
            this.setState({ renderModal: true });
            this.openModal();
          }
        } else if (data.type === 'ping') {
          this.openOnsitePing();
        } else if (data.type === 'qualaroo' && data.data.pingArgs) {
          this.setupQualarooScript(
            data.data.pingArgs as { extraData: QualarooData },
          );
        }
      },
    );
  };

  // TODO(jacob): when generalizing to muliutple pages, pingPageParams needs to be updated
  openOnsitePing = () => {
    const { pageType } = this.props;
    const { data } = this.state;

    if (data == null) return;

    const pingComponent = onsitePings[data.pingName];

    if (DELAY_PINGS.includes(data.pingName)) {
      setTimeout(() => {
        renderPingWhenReady(pingComponent, data, { page_type: pageType });
      }, 5000);
    } else {
      renderPingWhenReady(pingComponent, data, { page_type: pageType });
    }
  };

  // TODO: Test and handle these separately for defer
  openModal = async () => {
    const { decision, data } = this.state;
    if (!data) return;
    const { work } = data as any;

    switch (decision) {
      case 'bulk_download':
        await this.fetchPostDownloadModalStateAndUpdate();
        // NATIONAL PARKS: Please remove this disable and try to fix it if you're here
        // eslint-disable-next-line no-case-declarations
        const trigger =
          // NATIONAL PARKS: Please remove this disable and try to fix it if you're here
          // eslint-disable-next-line eqeqeq
          campaign == 'claiming-recommended-papers-download-button'
            ? 'claiming-recommended-papers-download-button'
            : 'swp-auto-download';
        // This is how the bulk download modal is shown for auto=download
        showBulkDownloadModal({
          trigger: trigger,
          gatedDownload: true,
          work,
          readerMessageLimitReached: this.state.readerMessageLimitReached,
          setReaderMessageLimitReached: this.setReaderMessageLimitReached,
          citerightMarketingModalEligible:
            this.state.citerightMarketingModalEligible,
        });
        return;
    }
  };

  setupQualarooScript = (args: { extraData: QualarooData }) => {
    const { data } = this.state;

    if (data == null) return;
    // Inserts Qualaroo scripts and user params that we need to send to Qualaroo (3rd party service)
    // Sign into Qualaroo to see snippet: https://app.qualaroo.com/sites/70993/install/paste_code
    // NATIONAL PARKS: Please remove this disable and try to fix it if you're here
    // eslint-disable-next-line no-var
    var qualarooScript = document.createElement('script');
    qualarooScript.type = 'text/javascript';
    startQualaroo(args.extraData, this.qualarooFailCallback);
  };

  // Qualaroo may not have a survey for the user.
  // If it doesn't, requeset a second interruption choice.
  qualarooFailCallback = () => {
    if (this.state.qualarooFailed) return;

    this.fetchInterruptionChoice(true);
    this.setState({ qualarooFailed: true });
  };

  fetchPostDownloadModalStateAndUpdate = async () => {
    // workId is only available on SWP pages
    if (this.props.workId == null) return;

    const state = await fetchPostDownloadModalState(
      this.props.workId.toString(),
    );

    this.setReaderMessageLimitReached(state.readerMessageLimitReached);
    this.setState({
      citerightMarketingModalEligible: state.citerightMarketingModalEligible,
    });
  };

  setReaderMessageLimitReached = (readerMessageLimitReached: boolean) => {
    this.setState({ readerMessageLimitReached: readerMessageLimitReached });
  };
}
